import { Box, Button, DesktopModal, Flex, IconFont, Input, Text } from '@byecode/ui'
import type { ClickTriggerConfig, FlowNode, VariableTriggerConfig } from '@lighthouse/shared'
import {
    getNodeDataSourceId,
    innerTypeToFieldTypeIconMap,
    OUTPUT_SINGLE_ARGS_NODES,
    OUTPUT_SINGLE_RECORD_NODES,
    SYSTEM_FIELD
} from '@lighthouse/shared'
import { useReactFlow, useStore } from '@xyflow/react'
import { memo, useMemo } from 'react'
import styled from 'styled-components'

import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

const SCTips = styled.div`
    margin-bottom: 12px;
    padding: 8px;
    border-radius: 6px;
    background-color: var(--color-purple-100);
    font-size: 12px;
    line-height: 20px;
    color: var(--color-main);
`

const FlowTest = memo(() => {
    const { getNodes } = useReactFlow()

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const datasourceList = useDataSourceList(appId, envId)

    const fields = useMemo(() => {
        const nodes = getNodes()
        const beginNode = nodes[0] as FlowNode

        const isAcceptRecordNode = OUTPUT_SINGLE_RECORD_NODES.has(beginNode.data.nodeType)
        const isAcceptArgsNode = OUTPUT_SINGLE_ARGS_NODES.has(beginNode.data.nodeType)

        if (isAcceptRecordNode) {
            const dsId = getNodeDataSourceId(beginNode, [])
            const ds = datasourceList.find(item => item.id === dsId)
            if (!ds) {
                return []
            }

            return Object.values(ds.schema).filter(field => !SYSTEM_FIELD.has(field.type))
        }

        if (isAcceptArgsNode) {
            const { args } = beginNode.data.config as ClickTriggerConfig | VariableTriggerConfig
            return args
        }

        return []
    }, [datasourceList, getNodes])

    return (
        <>
            <SCTips>请填入一条用于测试的数据</SCTips>
            {fields.map(item => (
                <Flex key={item.id} gap={6} direction="column" py={8}>
                    <Flex gap={6} alignItems="center">
                        <IconFont type={innerTypeToFieldTypeIconMap[item.innerType]} />
                        <Text>{item.name}</Text>
                    </Flex>
                    <Input />
                </Flex>
            ))}

            <Box style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 16 }}>
                <Button block type="primary" size="lg">
                    开始运行
                </Button>
            </Box>
        </>
    )
})

export const FlowTestModal = () => {
    const rootEl = useStore(s => s.domNode)
    const { setModalState } = useFlowContainerContext()

    return (
        <DesktopModal
            open
            onClose={() => setModalState({})}
            title="测试运行"
            withOverlay={false}
            closeOnClickOverlay={false}
            trapFocus={false}
            width={360}
            target={rootEl}
            styles={{
                root: { pointerEvents: 'none' },
                modal: {
                    pointerEvents: 'initial',
                    position: 'absolute',
                    top: 60,
                    right: 12,
                    bottom: 12
                },
                body: {
                    padding: '16px 16px 68px 16px !important'
                }
            }}
            transition={{ from: { x: '100%' }, to: { x: '0%' } }}
        >
            <FlowTest />
        </DesktopModal>
    )
}
